/**
 * Medical Scribe Streaming Provider - Thin composition layer
 *
 * This provider composes together:
 * - WebSocket connection management
 * - Audio processing
 * - Recording control (start/stop/pause/resume)
 * - State synchronization with Redux
 */
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import * as Comlink from "comlink";
import { Editor, type Bookmark } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  AMBIENT_SCRIBE_VERIFIED_MICROPHONES,
  MEDICAL_SCRIBE_FEATURE
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { selectCurrentUserToken } from "features/auth/auth-slice";
import useCheckPaywallFeatureAvailability from "features/paywall/use-check-paywall-feature-availability.hook";
import { LAST_FOCUSED_WORD_PROCESSOR_FIELD } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import {
  type SyncMessage,
  appInstanceId
} from "hooks/use-sync-between-tabs.hook";
import useSyncBetweenTabs from "hooks/use-sync-between-tabs.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { setFavicon } from "utils/set-favicon.util";

import InsertScribeDialogHost from "./components/insert-scribe-dialog-host.component";
import MedicalScribeConfirmationDialog from "./components/medical-scribe-confirmation-dialog.component";
import MedicalScribeSilenceWarningDialog from "./components/medical-scribe-silence-warning-dialog.component";
import { createLogger } from "./logging.util";
import { RECORDING_STATE_IN_PROGRESS } from "./medical-scribe-constants";
import { TAB_SYNC_ACTIONS } from "./medical-scribe-constants";
import type { MedicalScribeContextType } from "./medical-scribe-context-type.type";
import MedicalScribeRecordingWidget from "./medical-scribe-recording-widget";
import {
  type TranscriptEntry,
  pauseScribeRecording,
  selectMedicalScribeRecordingState,
  selectStreamingScribeId,
  selectStreamingTranscript,
  setShowSilenceWarningDialog,
  setStreamingConnecting,
  setStreamingPaused,
  setStreamingScribeId
} from "./medical-scribe.slice";
import type { MicrophoneInput } from "./microphone.utils";
import AudioWorker from "./save-audio-worker/save-audio-worker?worker";
import { useAudioProcessor } from "./streaming/hooks/use-audio-processor";
import { useFrontendSilenceDetection } from "./streaming/hooks/use-frontend-silence-detection.hook";
import { useRecordingControl } from "./streaming/hooks/use-recording-control";
import type { FinalizePauseOptions } from "./streaming/hooks/use-recording-control";
import { useWebSocketConnection } from "./streaming/hooks/use-websocket-connection";
import { isAppointmentV2Page } from "./utils";

export const MedicalScribeStreamingContext =
  createContext<MedicalScribeContextType | null>(null);

type MedicalScribeStreamingProviderProps = {
  children?: ReactNode;
};

export const MedicalScribeStreamingProvider = ({
  children
}: MedicalScribeStreamingProviderProps) => {
  const { isSunoAdminOrBusinessOwner, isAudiologist } = useUserRoles();
  const flags = useFlags();
  const isMedicalScribeEnabled =
    flags[MEDICAL_SCRIBE_FEATURE] &&
    (isSunoAdminOrBusinessOwner || isAudiologist);

  const dispatch = useAppDispatch();
  const location = useLocation();

  // Get auth token and state from Redux
  const token = useAppSelector(selectCurrentUserToken);
  const scribeId = useAppSelector(selectStreamingScribeId);
  const transcript = useAppSelector(selectStreamingTranscript);
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);
  const isRecordingInProgress = recordingState === RECORDING_STATE_IN_PROGRESS;

  // WebSocket refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectDelayRef = useRef<number>(2000);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Audio worker refs for chunk saving
  const audioWorkerRef = useRef<Comlink.Remote<any> | null>(null);
  const audioWorkerInstanceRef = useRef<Worker | null>(null);
  const audioRecordingIdRef = useRef<string | null>(null); // Track recording ID across pause/resume

  // State refs
  const isRecordingRef = useRef<boolean>(false);
  const isConnectingRef = useRef<boolean>(false);
  const isPausingRef = useRef<boolean>(false);
  const isStoppingRef = useRef<boolean>(false);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingParamsRef = useRef<{
    patientId: number;
    chartNoteId: number | undefined;
  } | null>(null);
  const scribeIdRef = useRef<number | undefined>(undefined);
  const pauseStartTimeRef = useRef<number | null>(null);
  const totalPausedDurationRef = useRef<number>(0);

  // Transcript and reconnection refs
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const lastInterimRef = useRef<TranscriptEntry | null>(null);
  const startRecordingInternalRef = useRef<
    | ((
        patientId: number,
        chartNoteId: number | undefined,
        options?: import("./streaming/types").StartRecordingInternalOptions
      ) => Promise<boolean>)
    | null
  >(null);
  const finalizePauseRef = useRef<
    ((options?: FinalizePauseOptions) => Promise<void>) | null
  >(null);
  const closeWebSocketRef = useRef<
    ((reason: string) => Promise<void>) | null
  >(null);
  const resumeInFlightRef = useRef(false);
  // Stores the fingerprint of the currently active recording session so
  // continue_recording messages can include the microphone payload.
  const microphoneFingerprintRef = useRef<MicrophoneInput | undefined>(
    undefined
  );

  // Track if there's an active recording on another tab
  const [isRecordingActiveOnAnotherTab, setIsRecordingActiveOnAnotherTab] =
    useState(false);
  const processedMessageIds = useRef(new Set<string>());

  // Handle cross-tab messages
  const handleTabSyncMessage = useCallback(
    async (message: SyncMessage & { action: string; payload?: any }) => {
      const { emitterId, action } = message;

      // Ignore messages from this tab
      if (emitterId === appInstanceId) return;

      createLogger.log(
        `Received streaming scribe tab sync message: action=${action}`,
        null
      );

      switch (action) {
        case TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB:
        case TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB:
          setIsRecordingActiveOnAnotherTab(true);
          break;
        case TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB:
          // Keep widget hidden for paused state too
          setIsRecordingActiveOnAnotherTab(true);
          break;
        case TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB:
          setIsRecordingActiveOnAnotherTab(false);
          break;
        case TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS: {
          // Another tab is asking if we have an active recording
          // Respond if we do
          try {
            const { getRecordingSession } = await import(
              "./recording-session.util"
            );
            const { RECORDING_STATE_IN_PROGRESS, RECORDING_STATE_PAUSED } =
              await import("./medical-scribe-constants");
            const { dispatchTabSyncMessage } = await import(
              "hooks/use-sync-between-tabs.hook"
            );

            const session = await getRecordingSession();
            if (
              session?.tabInstanceId === appInstanceId &&
              (session.recordingState === RECORDING_STATE_IN_PROGRESS ||
                session.recordingState === RECORDING_STATE_PAUSED)
            ) {
              createLogger.log(
                "Responding to recording status request - this tab owns an active recording",
                null
              );
              dispatchTabSyncMessage({
                action: TAB_SYNC_ACTIONS.RECORDING_STATUS_RESPONSE,
                payload: { ownerTabId: appInstanceId }
              });
            }
          } catch (error) {
            createLogger.error(
              "Error responding to recording status request:",
              error,
              null
            );
          }
          break;
        }
        default:
          break;
      }
    },
    []
  );

  // Listen for messages from other tabs
  useSyncBetweenTabs<SyncMessage & { action: string; payload?: any }>(
    processedMessageIds,
    handleTabSyncMessage,
    !isMedicalScribeEnabled
  );

  // Sync refs with Redux state
  useEffect(() => {
    transcriptRef.current = transcript || [];
  }, [transcript]);

  useEffect(() => {
    scribeIdRef.current = scribeId;
  }, [scribeId]);

  // For compatibility with old MediaRecorder interface
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  // Editor ref for TinyMCE
  const editorRef = useRef<Editor | null | undefined>(undefined);
  const insertScribeSelectionBookmarkRef = useRef<Bookmark | null>(null);
  const setEditorRef = useCallback((editor: Editor | undefined | null) => {
    editorRef.current = editor;
  }, []);

  useEventBus(LAST_FOCUSED_WORD_PROCESSOR_FIELD, (editor: Editor) => {
    editorRef.current = editor;
  });

  useEffect(() => {
    if (location.pathname) {
      setEditorRef(null);
    }
  }, [location, setEditorRef]);

  // Cleanup function for all resources
  const cleanupResources = useCallback(() => {
    createLogger.log(
      "🧹 Cleaning up resources...",
      recordingStartTimeRef.current
    );

    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Cleanup audio resources (handled by audio processor hook)
    // Cleanup WebSocket (handled by WebSocket hook)

    // Close WebSocket
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close(1000, "Cleanup");
        }
      } catch (err) {
        createLogger.warn(
          `⚠️ Error closing WebSocket: ${err instanceof Error ? err.message : String(err)}`,
          recordingStartTimeRef.current
        );
      }
      wsRef.current = null;
    }

    // Reset reconnection state
    reconnectAttemptsRef.current = 0;
    reconnectDelayRef.current = 2000;
    recordingStartTimeRef.current = null;
    isRecordingRef.current = false;
    dispatch(setStreamingConnecting(false));
  }, [dispatch]);

  // Group refs for easier passing - use useMemo to keep stable references
  const wsRefs = useMemo(
    () => ({
      ws: wsRef,
      reconnectTimeout: reconnectTimeoutRef,
      reconnectAttempts: reconnectAttemptsRef,
      reconnectDelay: reconnectDelayRef
    }),
    []
  );

  const audioRefs = useMemo(
    () => ({
      audioContext: audioContextRef,
      processor: processorRef,
      source: sourceRef,
      stream: streamRef
    }),
    []
  );

  const stateRefs = useMemo(
    () => ({
      isRecording: isRecordingRef,
      isConnecting: isConnectingRef,
      isPausing: isPausingRef,
      isStopping: isStoppingRef,
      recordingStartTime: recordingStartTimeRef,
      recordingParams: recordingParamsRef,
      scribeId: scribeIdRef,
      pauseStartTime: pauseStartTimeRef,
      totalPausedDuration: totalPausedDurationRef
    }),
    []
  );

  // Initialize audio processor hook
  const {
    cleanupAudioResources,
    teardownAudioNodes,
    pauseAudioGraph,
    pendingChunkSavePromiseRef,
    setupAudioPipeline,
    getMicrophoneStream,
    startVolumeAnalysis,
    stopVolumeAnalysis,
    startChunkSaving,
    stopChunkSaving,
    stopMediaStream,
    flushAudioWorklet,
    armPhantomSessionWatchdog
  } = useAudioProcessor({
    dispatch,
    wsRefs,
    audioRefs,
    stateRefs,
    audioWorkerRef
  });

  // Complete cleanup including audio
  const completeCleanup = useCallback(async () => {
    await cleanupAudioResources();
    cleanupResources();
  }, [cleanupAudioResources, cleanupResources]);

  // Store cleanup function in ref to avoid re-running cleanup effect when it changes
  const completeCleanupRef = useRef(completeCleanup);
  useEffect(() => {
    completeCleanupRef.current = completeCleanup;
  }, [completeCleanup]);

  // Initialize audio worker for chunk saving
  useEffect(() => {
    if (!isMedicalScribeEnabled) return;

    const audioWorkerInstance = new AudioWorker();
    audioWorkerInstanceRef.current = audioWorkerInstance;
    audioWorkerRef.current = Comlink.wrap(audioWorkerInstance);

    createLogger.log("Audio worker initialized for streaming", null);

    return () => {
      if (audioWorkerInstanceRef.current) {
        audioWorkerInstanceRef.current.terminate();
        audioWorkerInstanceRef.current = null;
        audioWorkerRef.current = null;
      }
    };
  }, [isMedicalScribeEnabled]);

  const { onTranscription, restartTimer: restartSilenceTimer } =
    useFrontendSilenceDetection({
      isRecording: isRecordingInProgress,
      onSilenceDetected: () => dispatch(setShowSilenceWarningDialog(true)),
      isEnabled: true
    });

  const { connectWebSocket, authenticate, sendContinueRecording } =
    useWebSocketConnection({
      dispatch,
      token,
      wsRefs,
      stateRefs,
      transcriptRef,
      lastInterimRef,
      mediaRecorderRef,
      audioWorkerRef,
      audioRecordingIdRef,
      startRecordingInternalRef,
      microphoneFingerprintRef,
      cleanupResources: completeCleanup,
      startVolumeAnalysis,
      armPhantomSessionWatchdog,
      startChunkSaving,
      stopChunkSaving,
      onTranscriptionReceived: onTranscription,
      finalizePauseRef,
      closeWebSocketRef,
      resumeInFlightRef
    });

  const {
    startRecording,
    startRecordingInternal,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetAfterPaused,
    resumePausedScribe,
    prepareForResumeFromEditScribe,
    backgroundCleanupInFlight
  } = useRecordingControl({
    dispatch,
    wsRefs,
    audioRefs,
    stateRefs,
    lastInterimRef,
    mediaRecorderRef,
    audioWorkerRef,
    audioRecordingIdRef,
    connectWebSocket,
    authenticate,
    setupAudioPipeline,
    getMicrophoneStream,
    cleanupResources: completeCleanup,
    teardownAudioNodes,
    pauseAudioGraph,
    pendingChunkSavePromiseRef,
    startVolumeAnalysis,
    stopVolumeAnalysis,
    startChunkSaving,
    stopChunkSaving,
    stopMediaStream,
    flushAudioWorklet,
    isVerifiedMicrophonesEnabled: Boolean(
      flags[AMBIENT_SCRIBE_VERIFIED_MICROPHONES]
    ),
    microphoneFingerprintRef,
    finalizePauseRef,
    closeWebSocketRef,
    resumeInFlightRef
  });

  // Sync startRecordingInternal to ref for reconnection logic
  useEffect(() => {
    startRecordingInternalRef.current = startRecordingInternal;
  }, [startRecordingInternal]);

  // Cleanup on unmount only - use ref to avoid re-running when completeCleanup changes
  useEffect(() => {
    return () => {
      // Fire-and-forget cleanup on unmount (can't await in cleanup function)
      void completeCleanupRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  // Check for in-progress sessions on mount (page reload recovery)
  useEffect(() => {
    if (!isMedicalScribeEnabled) return;

    let timeoutId: NodeJS.Timeout | null = null;
    let cleanupHandler: ((event: MessageEvent) => void) | null = null;
    let isMounted = true;

    const checkForInProgressSession = async () => {
      try {
        const { getRecordingSession, saveRecordingSession } = await import(
          "./recording-session.util"
        );
        const { RECORDING_STATE_IN_PROGRESS, RECORDING_STATE_PAUSED } =
          await import("./medical-scribe-constants");
        const { dispatchTabSyncMessage, syncChannel } = await import(
          "hooks/use-sync-between-tabs.hook"
        );

        const session = await getRecordingSession();

        // Only restore if we have a session with scribeId (streaming mode)
        if (
          session?.scribeId &&
          (session.recordingState === RECORDING_STATE_IN_PROGRESS ||
            session.recordingState === RECORDING_STATE_PAUSED)
        ) {
          createLogger.log(
            `Found in-progress streaming session on page reload: scribeId=${session.scribeId}, tabInstanceId=${session.tabInstanceId}, currentTabId=${appInstanceId}`,
            null
          );

          // Check if the recording belongs to this tab or another tab
          if (
            session.tabInstanceId &&
            session.tabInstanceId !== appInstanceId
          ) {
            // Recording might belong to another tab - check if that tab is still alive
            createLogger.log(
              `Session belongs to another tab (ID: ${session.tabInstanceId}), checking if that tab is still active...`,
              null
            );

            let ownerTabIsAlive = false;

            const handleStatusResponse = (event: MessageEvent) => {
              try {
                const data = JSON.parse(event.data);
                if (
                  data.action === TAB_SYNC_ACTIONS.RECORDING_STATUS_RESPONSE &&
                  data.emitterId === session.tabInstanceId
                ) {
                  ownerTabIsAlive = true;
                  createLogger.log(
                    "Owner tab is still alive, hiding widget on this tab",
                    null
                  );
                  if (isMounted) {
                    setIsRecordingActiveOnAnotherTab(true);
                  }
                }
              } catch {}
            };

            // Store handler reference for cleanup
            cleanupHandler = handleStatusResponse;
            syncChannel.addEventListener("message", handleStatusResponse);

            // Request status from the owner tab
            dispatchTabSyncMessage({
              action: TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS,
              payload: { requestingTabId: appInstanceId }
            });

            // Wait 2 seconds for response
            timeoutId = setTimeout(async () => {
              syncChannel.removeEventListener("message", handleStatusResponse);
              cleanupHandler = null;

              // Check if component is still mounted before proceeding
              if (!isMounted) {
                createLogger.log(
                  "Component unmounted, skipping orphaned session handling",
                  null
                );
                return;
              }

              if (!ownerTabIsAlive) {
                // No response - the owner tab is closed, this tab can claim ownership
                createLogger.log(
                  "No response from owner tab - session is orphaned. This tab will claim ownership.",
                  null
                );

                // Claim ownership by updating session with this tab's ID
                await saveRecordingSession({
                  ...session,
                  tabInstanceId: appInstanceId
                });

                // Restore the session on this tab
                if (isMounted) {
                  // Restore scribeId to state
                  scribeIdRef.current = session.scribeId;
                  dispatch(setStreamingScribeId(session.scribeId));

                  // Restore recording params
                  recordingParamsRef.current = {
                    patientId: session.patientId,
                    chartNoteId: session.chartNoteId
                  };

                  // Restore audio recording ID for continuing the same recording
                  if (session.audioRecordingId) {
                    audioRecordingIdRef.current = session.audioRecordingId;
                    createLogger.log(
                      `✅ Restored audioRecordingId: ${session.audioRecordingId}`,
                      null
                    );
                  }

                  // Set to paused state so user can manually resume
                  dispatch(setStreamingPaused(true));
                  dispatch(pauseScribeRecording());

                  const { updateRecordingSessionState } = await import(
                    "./recording-session.util"
                  );
                  const { FAVICON_PATHS } = await import(
                    "./medical-scribe-constants"
                  );
                  await updateRecordingSessionState(RECORDING_STATE_PAUSED);
                  setFavicon(FAVICON_PATHS.PAUSED);

                  // Clear the flag to show the widget
                  setIsRecordingActiveOnAnotherTab(false);

                  createLogger.log(
                    "✅ Streaming session restored to paused state. User can click Resume to continue.",
                    null
                  );
                }
              }
            }, 2000);
          } else {
            // Recording belongs to this tab (after reload) or has no tabInstanceId (old session)
            createLogger.log("Session belongs to this tab, restoring...", null);

            // Restore scribeId to state
            scribeIdRef.current = session.scribeId;
            dispatch(setStreamingScribeId(session.scribeId));

            // Restore recording params
            recordingParamsRef.current = {
              patientId: session.patientId,
              chartNoteId: session.chartNoteId
            };

            // Restore audio recording ID for continuing the same recording
            if (session.audioRecordingId) {
              audioRecordingIdRef.current = session.audioRecordingId;
              createLogger.log(
                `✅ Restored audioRecordingId: ${session.audioRecordingId}`,
                null
              );
            }

            // Set to paused state so user can manually resume
            dispatch(setStreamingPaused(true));
            dispatch(pauseScribeRecording());

            // Update session state to paused if it was in progress
            if (session.recordingState === RECORDING_STATE_IN_PROGRESS) {
              const { updateRecordingSessionState } = await import(
                "./recording-session.util"
              );
              const { FAVICON_PATHS } = await import(
                "./medical-scribe-constants"
              );
              await updateRecordingSessionState(RECORDING_STATE_PAUSED);
              setFavicon(FAVICON_PATHS.PAUSED);
            }

            createLogger.log(
              "✅ Streaming session restored to paused state. User can click Resume to continue.",
              null
            );
          }
        }
      } catch (error) {
        createLogger.error(
          "Failed to check for in-progress session on mount:",
          error,
          null
        );
      }
    };

    checkForInProgressSession();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;

      // Clear timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Remove event listener if it exists
      if (cleanupHandler) {
        (async () => {
          const { syncChannel } = await import(
            "hooks/use-sync-between-tabs.hook"
          );
          syncChannel.removeEventListener("message", cleanupHandler);
        })();
      }
    };
  }, [dispatch, isMedicalScribeEnabled]);

  // Feature availability check
  const checkFeatureAvailabilityAsync = useCheckPaywallFeatureAvailability();
  const [isAmbientScribeFeatureAvailable, setIsAmbientScribeFeatureAvailable] =
    useState(false);

  useEffect(() => {
    checkFeatureAvailabilityAsync(AMBIENT_SCRIBE_FEATURE_KEY).then(
      ({ isAvailable }) => {
        setIsAmbientScribeFeatureAvailable(isAvailable);
      }
    );
  }, [checkFeatureAvailabilityAsync]);

  const isChartNotesPage = useMemo(() => {
    const chartNotesRegex = /^\/patients\/\d+(\/chart-notes\/?)?\/?$/;
    return chartNotesRegex.test(location.pathname);
  }, [location.pathname]);

  const isMedicalScribesPage = useMemo(() => {
    return /^\/patients\/\d+\/medical-scribes\/?$/.test(location.pathname);
  }, [location.pathname]);

  const isAppointmentV2PageActive = useMemo(
    () => isAppointmentV2Page(flags, location.pathname),
    [flags, location.pathname]
  );

  // Confirmation dialog handlers
  const handleConfirmation = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const handleCancel = useCallback(() => {
    resumeRecording();
  }, [resumeRecording]);

  // Silence warning dialog handlers
  const handleSilenceWarningClose = useCallback(() => {
    dispatch(setShowSilenceWarningDialog(false));
  }, [dispatch]);

  const handleSilenceWarningContinue = useCallback(() => {
    // User chose "No" (doesn't want to pause) - send continue_recording to backend
    sendContinueRecording();
    dispatch(setShowSilenceWarningDialog(false));
    // Restart the FE silence detection window so it doesn't remain dead after dismissal
    restartSilenceTimer();
  }, [dispatch, sendContinueRecording, restartSilenceTimer]);

  // Context value
  const contextValue = useMemo(
    () => ({
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      resumePausedScribe,
      resetAfterPaused,
      prepareForResumeFromEditScribe,
      mediaRecorderRef,
      editorRef,
      setEditorRef,
      insertScribeSelectionBookmarkRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable,
      backgroundCleanupInFlight
    }),
    [
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      resumePausedScribe,
      resetAfterPaused,
      prepareForResumeFromEditScribe,
      setEditorRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable,
      backgroundCleanupInFlight
    ]
  );

  return (
    <MedicalScribeStreamingContext.Provider value={contextValue}>
      {children}
      {isMedicalScribeEnabled && !isRecordingActiveOnAnotherTab && (
        <MedicalScribeRecordingWidget
          mode={isChartNotesPage ? "fixed" : "draggable"}
          rightAnchorElementSelector=".chart-notes-buttons-container"
          isFullSize={
            isChartNotesPage ||
            isMedicalScribesPage ||
            isAppointmentV2PageActive
          }
          suppressInsertScribeDialog={isAppointmentV2PageActive}
        />
      )}
      <MedicalScribeConfirmationDialog
        handlePauseRecording={pauseRecording}
        onConfirm={handleConfirmation}
        onCancel={handleCancel}
      />
      <MedicalScribeSilenceWarningDialog
        onClose={handleSilenceWarningClose}
        onContinue={handleSilenceWarningContinue}
        onPause={pauseRecording}
      />
      {isMedicalScribeEnabled && isAppointmentV2PageActive && (
        <InsertScribeDialogHost />
      )}
    </MedicalScribeStreamingContext.Provider>
  );
};
